import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { format, setHours, setMilliseconds, setMinutes, setSeconds, startOfMonth, startOfWeek, startOfYear, subDays } from 'date-fns';

import { UseOutletContextContact } from './Contact';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { AccountingEntryDA } from '../../../services/admin/accounting.entry.service';
import { ApiDigitalAdminContact } from '../../../services/admin/contact.service';
import { AccountingEntry } from '../../../models/accounting.entry.model';
import { Contact } from '../../../models/contact.model';
import { formatContact, formatMoney } from '../../../scripts/format.value.script';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { modalShow } from '../../../utils/modal.util';
import { resolveDocumentRoute } from '../../../scripts/document.link.script';
import { entryTypeLabelKey } from '../../../scripts/entry.type.label.script';

import AppModalContactCreateAccountingEntry from '../../../components/modal/ModalContactCreateAccountingEntry';
import AppModalContactEntryExport from '../../../components/modal/ModalContactExportAccountingEntry';
import AppStorageRemove from '../../../components/element/StorageRemove';
import AppPagination from '../../../components/element/Pagination';
import AppPreloader from '../../../components/Preloader';
import AppLoading from '../../../components/Loading';
import ButtonEntryDocuments from '../../../components/button/ButtonEntryDocuments';
import imgSearch from '../../../assets/themes/images/search.svg';
import EmptyState from '../../../components/element/EmptyState';

export interface ContactAccountPageProps {};

function describeEntryLine(line: any, entry: any, lang: any): string {
  // Use the stored description as the source of truth when available
  if (entry.description) return entry.description

  if (!line) return ''

  const ref = line.reference ?? {}
  const lineType = line.type ?? ''
  const docNum = ref.document_number ?? ''

  // Fallback: regenerate label for legacy entries without description
  if (ref.id_invoice) {
    return [lang.labels.invoiceIssuance ?? 'Facture', ref.invoice_number ?? docNum].filter(Boolean).join(' ').trim()
  }
  if (ref.id_sale) {
    return [lang.labels.saleIssuance ?? 'Vente', ref.sale_number ?? docNum].filter(Boolean).join(' ').trim()
  }
  if (ref.id_provider_invoice) {
    return [lang.labels.providerInvoiceIssuance ?? 'Facture fournisseur', ref.provider_invoice_number ?? docNum].filter(Boolean).join(' ').trim()
  }
  if (ref.id_expense) {
    return [lang.labels.expenseIssuance ?? 'Note de frais', ref.expense_number ?? docNum].filter(Boolean).join(' ').trim()
  }
  if (ref.id_payment || ref.id_treasury_account || (ref.id_payments?.length ?? 0) > 0) {
    const label = lang.labels[entryTypeLabelKey('payment', docNum)] ?? lang.labels.paymentReceived ?? 'Paiement'
    return [label, docNum].filter(Boolean).join(' ')
  }
  if (ref.id_reminder) {
    return lang.labels.allocationInvoiceReminder ?? 'Relance'
  }

  if (lineType) {
    const label = lang.labels[entryTypeLabelKey(lineType, docNum)] ?? lineType
    return [label, docNum].filter(Boolean).join(' ')
  }

  return line.description || ''
}

const ContactEntryPage: React.FunctionComponent<ContactAccountPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, accountingAccountsForCompany, accountingCreditEntriesForCompany, accountingDebitEntriesForCompany, treasuryAccountsForCompany, accountingExpenseAccountsForCompany, accountingIncomeAccountsForCompany, accountingTaxAccountsForCompany, contactsForCompany, loadAccountingCreditEntriesForCompany, loadAccountingDebitEntriesForCompany} = UseOutletContextContact()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [button, setButton] = useState(false)
  const [filter, setFilter] = useState({interval: '', start_date: '', end_date: ''})
  const [table, setTable] = useState<{head: any[], body: {date: string, description: string, debit: string, credit: string, balance: string, to: string | null, vouchers?: any[]}[], foot: any[]}>({head: [], body: [], foot: []})
  const [searchContact, setSearchContact] = useState<{text: string, list: Contact[]}>({text: '', list: []})
  const [accounitngEntries, setAccountingEntries] = useState<AccountingEntry[] | null>(null)
  const [transaction, setTransaction] = useState<string | null>(null)
  const [storage, setStorage] = useState<any>(null)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const searchAbortRef = useRef<AbortController | null>(null)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function resolveEntryRoute(entry: AccountingEntry): string | null {
    const line = entry.debits?.[0] ?? entry.credits?.[0]
    const ref = line?.reference
    if (!ref) return `/main/accounting/entry/view/${entry.id}`
    if (ref.id_invoice) return resolveDocumentRoute('invoice', ref.id_invoice)
    if (ref.id_provider_invoice) return resolveDocumentRoute('provider_invoice', ref.id_provider_invoice)
    if (ref.id_expense) return resolveDocumentRoute('expense', ref.id_expense)
    return `/main/accounting/entry/view/${entry.id}`
  }

  const loadAccountingContactEntries = async (id_company: string, id_contact: string, page: number = 1) => {
    let tempHead: {date: string, description: string, balance: string}[] = []
    let tempBody: {date: string, description: string, debit: string, credit: string, balance: string, to: string | null}[] = []
    let tempFoot: {date: string, description: string, debit: string, credit: string, balance: string}[] = []
    let endDate: string = (button && filter.end_date.length > 0) ? filter.end_date : format(setMilliseconds(setSeconds(setMinutes(setHours((new Date()), 23), 59), 59), 999), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)

    let debitTotal: number = 0
    let creditTotal: number = 0
    let balanceTotal: number = 0

    try {
      const response = await AccountingEntryDA.getAccountingContactEntries(id_company, id_contact, filter.interval, filter.start_date, filter.end_date, page, pagination.limit)

      if (response.status === 200 || response.status === 304) {
        const raw = response.data ?? {}

        // Normalize: extract entries from various response formats
        let entries: AccountingEntry[] = []
        let balancePrevious = { date: '', amount: 0 }
        let responseFilter = { interval: filter.interval, start_date: filter.start_date, end_date: filter.end_date }

        if (raw.accounting_entries) {
          // Legacy format: { id_company, id_contact, filter, balance_previous, accounting_entries }
          entries = raw.accounting_entries
          balancePrevious = raw.balance_previous ?? balancePrevious
          responseFilter = raw.filter ?? responseFilter
        } else if (Array.isArray(raw.items)) {
          // New format: { items, pagination }
          entries = raw.items
        } else if (Array.isArray(raw)) {
          // Direct array
          entries = raw
        }

        // Extract pagination
        const serverPagination = raw.pagination
        if (serverPagination) {
          const totalPages = serverPagination.pages ?? Math.ceil((serverPagination.total ?? 0) / (serverPagination.limit ?? 20))
          let pagesOfPagination: number[] = []
          for (let i = 1; i <= totalPages; i++) {
            pagesOfPagination.push(i)
          }
          setPagination(prev => ({...prev, current: serverPagination.page ?? page, total: serverPagination.total ?? 0, pages: pagesOfPagination}))
        } else {
          setPagination(prev => ({...prev, current: 1, total: entries.length, pages: [1]}))
        }

        localStorage.setItem('filter_contact_entries', JSON.stringify({
          company_id: id_company,
          contact_id: id_contact,
          interval: responseFilter.interval,
          start_date: responseFilter.start_date,
          end_date: responseFilter.end_date
        }))

        // Opening balance
        const openingBalance = raw.opening_balance ?? (balancePrevious.amount !== 0 ? { balance: balancePrevious.amount } : null)
        if (openingBalance && openingBalance.balance !== 0) {
          tempHead.push({
            date: balancePrevious.date ? dateFormat(lang.code, balancePrevious.date) : (filter.start_date ? dateFormat(lang.code, filter.start_date) : ''),
            description: lang.labels.balancePrevious,
            balance: formatMoney(openingBalance.balance)
          })
        }

        // Body: one row per entry
        let runningBalance = openingBalance?.balance ?? 0

        for (const entry of entries) {
          const debitLine = entry.debits?.[0]
          const creditLine = entry.credits?.[0]
          const debitAmount = debitLine?.amount ?? 0
          const creditAmount = creditLine?.amount ?? 0

          runningBalance += debitAmount - creditAmount

          const line = debitLine ?? creditLine
          const description = describeEntryLine(line, entry, lang)

          tempBody.push({
            date: dateFormat(lang.code, entry.date),
            description,
            debit: debitAmount > 0 ? formatMoney(debitAmount) : '',
            credit: creditAmount > 0 ? formatMoney(creditAmount) : '',
            balance: formatMoney(runningBalance),
            to: resolveEntryRoute(entry),
            vouchers: entry.vouchers,
          })
          debitTotal += debitAmount
          creditTotal += creditAmount
        }

        // Footer: period totals + closing balance
        const periodTotals = raw.period_totals ?? { debit: debitTotal, credit: creditTotal }
        const closingBalance = raw.closing_balance ?? { balance: runningBalance }
        balanceTotal = closingBalance.balance ?? runningBalance

        if (entries.length > 0) {
          tempFoot.push({date: dateFormat(lang.code, endDate), description: lang.labels.total, debit: formatMoney(periodTotals.debit), credit: formatMoney(periodTotals.credit), balance: ''})
          tempFoot.push({date: dateFormat(lang.code, endDate), description: (balanceTotal >= 0) ? lang.labels.balanceFavorContact : lang.labels.balanceFavorCompany, debit: '', credit: '', balance: formatMoney(Math.abs(balanceTotal))})
        }

        setButton(Boolean(responseFilter.interval || responseFilter.start_date || responseFilter.end_date))
        setTable({head: tempHead, body: tempBody, foot: tempFoot})
        setAccountingEntries(entries)
        setStorage((localStorage.getItem('filter_contact_entries')) ? JSON.parse(localStorage.getItem('filter_contact_entries')!) : null)
      } else {
        const data = response.data
        const isActualError = data?.success === false || data?.error || (response.status >= 400 && response.status < 600)

        if (isActualError) {
          errorResponse = data?.error ?? data
          GlobalSwal.fire({
            title: getError(errorResponse?.code, lang.code, errorResponse?.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        } else {
          console.warn('[ContactEntry] unexpected response shape (status:', response.status, '):', data)
        }

        setAccountingEntries([])
        setTable({head: [], body: [], foot: []})
      }
    } catch (error) {
      console.error('[ContactEntry] failed', error)
      setAccountingEntries([])
      setTable({head: [], body: [], foot: []})
    }
  }

  const handleChangeSearchContact = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    const text: string = event.target.value
    setSearchContact(prev => ({...prev, text}))

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (searchAbortRef.current) searchAbortRef.current.abort()

    if (!companyForUser) return

    if (text.length < 2) {
      // Below threshold: load default list
      searchTimerRef.current = setTimeout(() => {
        const abort = new AbortController()
        searchAbortRef.current = abort
        ApiDigitalAdminContact.getContacts(companyForUser.id, '', '', '', '', 1, 20, abort.signal).then(resp => {
          if (resp.status === 200 && resp.data) {
            const items = resp.data.items ?? resp.data.contacts ?? (Array.isArray(resp.data) ? resp.data : [])
            setSearchContact(prev => ({...prev, list: items}))
          }
        })
      }, 150)
      return
    }

    searchTimerRef.current = setTimeout(() => {
      const abort = new AbortController()
      searchAbortRef.current = abort
      ApiDigitalAdminContact.getContacts(companyForUser.id, text, '', '', '', 1, 20, abort.signal).then(resp => {
        if (resp.status === 200 && resp.data) {
          const items = resp.data.items ?? resp.data.contacts ?? (Array.isArray(resp.data) ? resp.data : [])
          setSearchContact(prev => ({...prev, list: items}))
        }
      })
    }, 300)
  }

  const handleChangeInterval = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    let startDate: Date = new Date()
    let endDate: Date = new Date()

    switch (event.target.value) {
      case 'today':
        setFilter({interval: event.target.value, start_date: format(setMilliseconds(setSeconds(setMinutes(setHours(startDate, 0), 0), 0), 0), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(setMilliseconds(setSeconds(setMinutes(setHours(endDate, 23), 59), 59), 999), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'yesterday':
        setFilter({interval: event.target.value, start_date: format(setMilliseconds(setSeconds(setMinutes(setHours(subDays(startDate, 1), 0), 0), 0), 0), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(setMilliseconds(setSeconds(setMinutes(setHours(subDays(endDate, 1), 23), 59), 59), 999), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'week':
        setFilter({interval: event.target.value, start_date: format(setMilliseconds(setSeconds(setMinutes(setHours(startOfWeek(startDate), 0), 0), 0), 0), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(setMilliseconds(setSeconds(setMinutes(setHours(endDate, 23), 59), 59), 999), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'month':
        setFilter({interval: event.target.value, start_date: format(setMilliseconds(setSeconds(setMinutes(setHours(startOfMonth(startDate), 0), 0), 0), 0), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(setMilliseconds(setSeconds(setMinutes(setHours(endDate, 23), 59), 59), 999), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'year':
        setFilter({interval: event.target.value, start_date: format(setMilliseconds(setSeconds(setMinutes(setHours(startOfYear(startDate), 0), 0), 0), 0), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(setMilliseconds(setSeconds(setMinutes(setHours(endDate, 23), 59), 59), 999), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      default:
        setFilter({interval: 'all', start_date: '', end_date: ''})
        break;
    }
  }

  const handleChangeStartDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''
    setFilter({...filter, interval: '', start_date: date})
  }

  const handleChangeEndDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T23:59:59.999`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''
    setFilter({...filter, interval: '', end_date: date})
  }

  const handleChangeContact = (item: string) => {
    navigate("/main/contact/entry/" + item)
  }

  const executeShowModalContactEntryExport = () => {
    if (param.id && param.id !== 'none') {
      modalShow('modal-contact-export-accounting-entry')
    }
  }

  const executeShowModalContactEntryCreate = (item: string) => {
    if (param.id && param.id !== 'none') {
      setTransaction(item)
      modalShow('modal-contact-create-accounting-entry')
    }
  }

  const executeFilterRemove = (item: string) => {
    if (companyForUser && param.id && param.id !== 'none' && (filter.interval || filter.start_date || filter.end_date).length !== 0) {
      for (let key of Object.keys(filter) as Array<keyof typeof filter>) {
        if (key === item) {
          filter[key] = ''
        }
        if (item === "start_date") {
          filter.interval = ''
        }
        if (item === "end_date") {
          filter.interval = ''
        }
        if (item === "interval") {
          filter.start_date = ''
          filter.end_date = ''
        }
      }
      loadAccountingContactEntries(companyForUser.id, param.id)
    }
  }

  const executeFilterApply = () => {
    if (companyForUser && param.id && param.id !== 'none') {
      loadAccountingContactEntries(companyForUser.id, param.id)
    }
  }

  const executeFilterReset = () => {
    if (companyForUser && param.id && param.id !== 'none') {
      filter.interval = ''
      filter.start_date = ''
      filter.end_date = ''
      loadAccountingContactEntries(companyForUser.id, param.id)
    }
  }

  const loadPagination = (current: number) => {
    if (!companyForUser || !param.id || param.id === 'none') return
    loadAccountingContactEntries(companyForUser.id, param.id, current)
  }

  function uploadDropdown() {
    setSearchContact({text: '', list: []})
    if (!companyForUser) return
    if (searchAbortRef.current) searchAbortRef.current.abort()
    const abort = new AbortController()
    searchAbortRef.current = abort
    ApiDigitalAdminContact.getContacts(companyForUser.id, '', '', '', '', 1, 20, abort.signal).then(resp => {
      if (resp.status === 200 && resp.data) {
        const items = resp.data.items ?? resp.data.contacts ?? (Array.isArray(resp.data) ? resp.data : [])
        setSearchContact({text: '', list: items})
      }
    })
  }

  useEffect( () => {
    if (companyForUser && param.id && param.id !== 'none') {
      setSelectedContact(null)
      ApiDigitalAdminContact.getContact(companyForUser.id, param.id).then(resp => {
        if (resp.status === 200 && resp.data) {
          setSelectedContact(resp.data)
        }
      })
    } else {
      setSelectedContact(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param.id])

  useEffect( () => {
    let filter_contact_entries: any = (localStorage.getItem('filter_contact_entries')) ? JSON.parse(localStorage.getItem('filter_contact_entries')!) : null

    if (companyForUser) {
      if (param.id && param.id !== 'none') {
        let dateCurrent: Date = setMilliseconds(setSeconds(setMinutes(setHours((new Date()), 23), 59), 59), 999)
        let yearCurrent: Date = setMilliseconds(setSeconds(setMinutes(setHours(startOfYear(dateCurrent), 0), 0), 0), 0)

        filter.interval = (filter_contact_entries && filter_contact_entries.company_id === companyForUser.id && filter_contact_entries.interval.length > 0) ? filter_contact_entries.interval : 'year'
        filter.start_date = (filter_contact_entries && filter_contact_entries.company_id === companyForUser.id) ? (filter_contact_entries.interval === 'all') ? '' : (filter_contact_entries.start_date.length > 0) ? filter_contact_entries.start_date : format(yearCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : format(yearCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
        filter.end_date = (filter_contact_entries && filter_contact_entries.company_id === companyForUser.id) ? (filter_contact_entries.interval === 'all') ? '' : (filter_contact_entries.end_date.length > 0) ? filter_contact_entries.end_date : format(dateCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : format(dateCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)

        setAccountingEntries(null)
        setTable({head: [], body: [], foot: []})

        loadAccountingContactEntries(companyForUser.id, param.id)
      } else if (filter_contact_entries && filter_contact_entries.company_id === companyForUser.id && filter_contact_entries.contact_id.length > 0) {
        navigate("/main/contact/entry/" + filter_contact_entries.contact_id)
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.contacts, branch: lang.labels.accountStatements}, company: false})

    return () => {
      setMounted(false)
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      if (searchAbortRef.current) searchAbortRef.current.abort()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <Dropdown onToggle={uploadDropdown}>
                  <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-350px p-0">
                    <span className="selection">
                      <span className="select2-selection select2-selection--single form-select">
                        <span className="select2-selection__rendered" role="textbox">
                          {param.id && param.id !== "none" ? (selectedContact ? formatContact(selectedContact) : '...') : lang.labels.searchSelectContact}
                        </span>
                      </span>
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                    <span className="select2-dropdown select2-dropdown--below">
                      <span className="select2-search select2-search--dropdown">
                        <input className="select2-search__field" type="text" autoComplete="off" name="contact" value={searchContact.text} onChange={handleChangeSearchContact} />
                      </span>
                      <span className="select2-results">
                        <ul className="select2-results__options" role="listbox">
                          { searchContact.list.length > 0
                            ?
                            <>
                              { searchContact.list.map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${param.id === item.id && "select2-results__option--selected"}`} role="option" aria-selected={param.id === item.id}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeContact(item.id)}>{formatContact(item)}</Dropdown.Item>
                                </li>
                              )}))}
                            </>
                            :
                            <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                          }
                        </ul>
                      </span>
                    </span>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-items-center">
                  <Dropdown>
                    <Dropdown.Toggle variant={`btn btn-hover-color-secondary ${button ? "btn-primary" : "btn-light-primary"} me-2`}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M19.0759 3H4.72777C3.95892 3 3.47768 3.83148 3.86067 4.49814L8.56967 12.6949C9.17923 13.7559 9.5 14.9582 9.5 16.1819V19.5072C9.5 20.2189 10.2223 20.7028 10.8805 20.432L13.8805 19.1977C14.2553 19.0435 14.5 18.6783 14.5 18.273V13.8372C14.5 12.8089 14.8171 11.8056 15.408 10.964L19.8943 4.57465C20.3596 3.912 19.8856 3 19.0759 3Z" fill="black" />
                          </svg>
                        </span>
                        {lang.labels.filter}
                      </div>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-250px w-md-300px" align="end">
                      <div className="mt-5 mx-5">
                        <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
                      </div>
                      <div className="separator border-gray-200"></div>
                      <div className="mt-3 m-5">
                        <div className="mb-3">
                          <label className="form-label fs-7">{lang.labels.timeInterval}:</label>
                          <Form.Select bsPrefix="form-select form-select-sm fw-bold" aria-placeholder={lang.labels.select} name="filter-interval" value={filter.interval} onChange={handleChangeInterval}>
                            <option value="" className="d-none">{lang.labels.select}</option>
                            <option value="all">{lang.labels.all}</option>
                            <option value="year">{lang.labels.thisYear}</option>
                            <option value="month">{lang.labels.thisMonth}</option>
                            <option value="week">{lang.labels.thisWeek}</option>
                            <option value="yesterday">{lang.labels.yesterday}</option>
                            <option value="today">{lang.labels.today}</option>
                          </Form.Select>
                        </div>
                        <div className="mb-3">
                          <label className="col-12 col-md-5 col-form-label fs-7 py-2">{lang.labels.startDate}:</label>
                          <input className="col-12 col-md-7 small-input bg-light border border-light rounded" type="date" name="filter-start-date" max={filter.end_date.substring(0, 10)} value={filter.start_date.substring(0, 10)} onChange={handleChangeStartDate} />
                        </div>
                        <div className="mb-5">
                          <label className="col-12 col-md-5 col-form-label fs-7 py-2">{lang.labels.endDate}:</label>
                          <input className="col-12 col-md-7 small-input bg-light border border-light rounded" type="date" name="filter-end-date" min={filter.start_date.substring(0, 10)} value={filter.end_date.substring(0, 10)} onChange={handleChangeEndDate} />
                        </div>
                        <div className="d-flex justify-content-end">
                          <Dropdown.Item bsPrefix="btn btn-sm btn-secondary btn-active-light-primary" onClick={executeFilterReset}>{lang.labels.reset}</Dropdown.Item>
                          <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={executeFilterApply}>{lang.labels.apply}</Dropdown.Item>
                        </div>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                  <button className="btn btn-primary me-2" type="button" onClick={executeShowModalContactEntryExport}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="12.75" y="4.25" width="12" height="2" rx="1" transform="rotate(90 12.75 4.25)" fill="black" />
                          <path d="M12.0573 6.11875L13.5203 7.87435C13.9121 8.34457 14.6232 8.37683 15.056 7.94401C15.4457 7.5543 15.4641 6.92836 15.0979 6.51643L12.4974 3.59084C12.0996 3.14332 11.4004 3.14332 11.0026 3.59084L8.40206 6.51643C8.0359 6.92836 8.0543 7.5543 8.44401 7.94401C8.87683 8.37683 9.58785 8.34458 9.9797 7.87435L11.4427 6.11875C11.6026 5.92684 11.8974 5.92684 12.0573 6.11875Z" fill="black" />
                          <path d="M18.75 8.25H17.75C17.1977 8.25 16.75 8.69772 16.75 9.25C16.75 9.80228 17.1977 10.25 17.75 10.25C18.3023 10.25 18.75 10.6977 18.75 11.25V18.25C18.75 18.8023 18.3023 19.25 17.75 19.25H5.75C5.19772 19.25 4.75 18.8023 4.75 18.25V11.25C4.75 10.6977 5.19771 10.25 5.75 10.25C6.30229 10.25 6.75 9.80228 6.75 9.25C6.75 8.69772 6.30229 8.25 5.75 8.25H4.75C3.64543 8.25 2.75 9.14543 2.75 10.25V19.25C2.75 20.3546 3.64543 21.25 4.75 21.25H18.75C19.8546 21.25 20.75 20.3546 20.75 19.25V10.25C20.75 9.14543 19.8546 8.25 18.75 8.25Z" fill="#C4C4C4" />
                        </svg>
                      </span>
                      {lang.labels.export}
                    </div>
                  </button>
                  <button className="btn btn-primary me-2" type="button" onClick={() => executeShowModalContactEntryCreate("debit")}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                          <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.addDebit}
                    </div>
                  </button>
                  <button className="btn btn-primary" type="button" onClick={() => executeShowModalContactEntryCreate("credit")}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                          <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.addCredit}
                    </div>
                  </button>
                </div>
              </div>
            </div>
            { storage && button &&
              <div className="card-scroll d-flex bg-light-primary rounded border-0 p-3 mx-7 mb-4">
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.interval.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("interval")}>
                  <AppStorageRemove attribute={lang.labels.interval} description={storage.interval}></AppStorageRemove>
                </button>
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.start_date.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("start_date")}>
                  <AppStorageRemove attribute={lang.labels.start} description={dateFormat(lang.code, storage.start_date)}></AppStorageRemove>
                </button>
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.end_date.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("end_date")}>
                  <AppStorageRemove attribute={lang.labels.end} description={dateFormat(lang.code, storage.end_date)}></AppStorageRemove>
                </button>
              </div>
            }
          </div>
          <div className="card">
            <div className="card-body p-0">
              <div className="dataTables_wrapper border border-2 rounded">
                <div className="table-responsive rounded">
                  <table className="table align-middle table-row-bordered gx-10 mb-2">
                    <thead className="bg-light border-bottom-2">
                      <tr className="text-dark text-uppercase fw-bolder fs-7">
                        <th className="w-100px text-start">{lang.labels.date}</th>
                        <th className="min-w-300px text-start">{lang.labels.description}</th>
                        <th className="min-w-150px text-end text-nowrap">{lang.labels.debit} ({companyForUser.currency?.code})</th>
                        <th className="min-w-150px text-end text-nowrap">{lang.labels.credit} ({companyForUser.currency?.code})</th>
                        <th className="min-w-150px text-end text-nowrap">{lang.labels.balance} ({companyForUser.currency?.code})</th>
                        <th className="w-50px text-center"><i className="bi bi-paperclip fs-4"></i></th>
                      </tr>
                    </thead>
                    { param && param.id && param.id !== "none"
                      ?
                      <>
                        { accounitngEntries
                          ?
                          <>
                            <tbody className="text-secondary fw-bold">
                              { table.head.map (( (item, index) => { return (
                                <tr key={index} className="row-bordered">
                                  <td className="text-nowrap">{item.date}</td>
                                  <td colSpan={4}>{item.description}</td>
                                  <td className="text-end text-nowrap">{item.balance}</td>
                                </tr>
                              )}))}
                            </tbody>
                            { table.body.length > 0
                              ?
                              <tbody className="text-secondary fw-bold">
                                { table.body.map (( (item, index) => { return (
                                  <tr key={index} className="row-bordered">
                                    <td className="text-nowrap">{item.date}</td>
                                    <td>
                                      { item.to
                                        ? <Link to={item.to} className="text-primary fw-bold text-hover-dark">{item.description}</Link>
                                        : item.description
                                      }
                                    </td>
                                    <td className="text-end text-nowrap">{item.debit}</td>
                                    <td className="text-end text-nowrap">{item.credit}</td>
                                    <td className="text-end text-nowrap">{item.balance}</td>
                                    <td className="text-center"><ButtonEntryDocuments vouchers={item.vouchers} /></td>
                                  </tr>
                                )}))}
                              </tbody>
                              :
                              <tbody>
                                <tr className="text-secondary fw-bold row-bordered">
                                  <td colSpan={6} className="text-center">{lang.labels.noRecordsFound}</td>
                                </tr>
                              </tbody>
                            }
                            <tfoot className="fw-bolder fs-7">
                              { table.foot.map (( (item, index) => { return (
                                <tr key={index}>
                                  <td className="text-nowrap">{item.date}</td>
                                  <td>{item.description}</td>
                                  <td className="text-end text-nowrap">{item.debit}</td>
                                  <td className="text-end text-nowrap">{item.credit}</td>
                                  <td className="text-end text-nowrap">{item.balance}</td>
                                </tr>
                              )}))}
                            </tfoot>
                          </>
                          :
                          <tbody>
                            <tr>
                              <td colSpan={6}>
                                <AppLoading></AppLoading>
                              </td>
                            </tr>
                          </tbody>
                        }
                      </>
                      :
                      <tbody>
                        <tr>
                          <td colSpan={5}>
                            <EmptyState illustration={imgSearch} illustrationAlt="" title={lang.labels.noRecordsFound} description={lang.labels.selectContactViewTheirAccountStatement} size="sm" />
                          </td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
              </div>
              { accounitngEntries && accounitngEntries.length > 0 && pagination.pages.length > 1 &&
                <div className="mt-5">
                  <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>
                </div>
              }
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalContactCreateAccountingEntry companyForUser={companyForUser} settingsForCompany={settingsForCompany} accountingAccountsForCompany={accountingAccountsForCompany} accountingCreditEntriesForCompany={accountingCreditEntriesForCompany} accountingDebitEntriesForCompany={accountingDebitEntriesForCompany} treasuryAccountsForCompany={treasuryAccountsForCompany} accountingExpenseAccountsForCompany={accountingExpenseAccountsForCompany} accountingIncomeAccountsForCompany={accountingIncomeAccountsForCompany} accountingTaxAccountsForCompany={accountingTaxAccountsForCompany} transaction={transaction} contact={param.id} loadAccountingCreditEntriesForCompany={loadAccountingCreditEntriesForCompany} loadAccountingContactEntries={loadAccountingContactEntries} loadAccountingDebitEntriesForCompany={loadAccountingDebitEntriesForCompany}></AppModalContactCreateAccountingEntry>
      <AppModalContactEntryExport companyForUser={companyForUser} contactsForCompany={contactsForCompany ?? []} table={table} contact={param.id}></AppModalContactEntryExport>
    </div>
  )
};

export default ContactEntryPage;
